import { useEffect, useRef, useState } from 'react';

// material-ui
import { Box, ClickAwayListener, ListItem, ListItemIcon, ListItemText, Paper, Popper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// project import
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { PlatformStatusService } from 'services';
import useAuth from 'hooks/useAuth';
import { Crisp } from 'crisp-sdk-web';

// assets
import supportIcon from 'assets/images/icons/header/support.svg';
import { FileTextOutlined, MessageOutlined, WhatsAppOutlined, SendOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { sizeByBreakpoint, textSizeByBreakpoint } from 'utils/helper';

// ==============================|| HEADER CONTENT - SUPPORT ||============================== //

const platformStatusService = new PlatformStatusService();

// Module-level cache to persist data across component remounts (e.g., theme changes)
let cachedSupportStatus: 'operational' | 'issues' | 'outage' = 'operational';
let hasFetchedSupportStatus = false;

const Support = () => {
  const theme = useTheme();
  const { accessToken } = useAuth();
  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<'operational' | 'issues' | 'outage'>(cachedSupportStatus);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await platformStatusService.getStatus(accessToken || null);
        // Determine status based on response
        let newStatus: 'operational' | 'issues' | 'outage' = 'operational';
        if (res.activeStatus.value === 'UP' || res.activeStatus.value === 'DEFAULT') {
          newStatus = 'operational';
        } else if (res.activeStatus.value.includes('MINOR') || res.activeStatus.value === 'HASISSUES') {
          newStatus = 'issues';
        } else {
          newStatus = 'outage';
        }
        cachedSupportStatus = newStatus;
        setStatus(newStatus);
      } catch (err) {
        console.error('Failed to fetch status:', err);
        setStatus('operational'); // Default to operational on error
      }
    };

    // Only fetch on first mount, not on theme changes
    if (!hasFetchedSupportStatus) {
      hasFetchedSupportStatus = true;
      fetchStatus();
    }

    const interval = setInterval(fetchStatus, 60000); // Refresh every minute
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMenuItemClick = (url: string) => {
    window.open(url, '_blank');
    setOpen(false);
  };

  const getStatusColor = () => {
    switch (status) {
      case 'operational':
        return theme.palette.success.main;
      case 'issues':
        return theme.palette.warning.main;
      case 'outage':
        return theme.palette.error.main;
      default:
        return theme.palette.success.main;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'operational':
        return 'All Systems Operational';
      case 'issues':
        return 'Experiencing Issues';
      case 'outage':
        return 'System Outage';
      default:
        return 'All Systems Operational';
    }
  };

  const CustomListItem = ({
    Icon,
    primaryText,
    onClick,
    showDot = false,
    dotColor
  }: {
    Icon: any;
    primaryText: string;
    onClick?: () => void;
    showDot?: boolean;
    dotColor?: string;
  }) => {
    return (
      <ListItem
        component="div"
        sx={{
          cursor: 'pointer',
          padding: 0,
          '&:hover': {
            backgroundColor: 'transparent',
            '& .MuiListItemIcon-root': {
              color: theme.palette.primary.main,
              transition: 'color 100ms ease-in-out'
            },
            '& .MuiListItemText-root': {
              color: theme.palette.primary.main,
              transition: 'color 100ms ease-in-out'
            }
          }
        }}
        onClick={onClick}
      >
        <ListItemIcon sx={{ minWidth: sizeByBreakpoint(1.5, 32) }}>
          <Box
            component={Icon}
            sx={{
              fontSize: textSizeByBreakpoint(18)
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={primaryText}
          primaryTypographyProps={{
            fontSize: textSizeByBreakpoint(14),
            fontWeight: 500
          }}
          sx={{
            margin: 0
          }}
        />
        {showDot && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: dotColor,
              ml: 1
            }}
          />
        )}
      </ListItem>
    );
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.5 }}>
      <IconButton
        color="secondary"
        sx={{ color: 'text.primary', bgcolor: 'transparent', '&:hover': { bgcolor: 'transparent' } }}
        aria-label="support"
        ref={anchorRef}
        aria-controls={open ? 'support-menu' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <img src={supportIcon} alt="support" style={{ width: 18, height: 18 }} />
      </IconButton>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            {open && (
              <Paper
                sx={{
                  boxShadow: theme.customShadows.headerDropdown,
                  marginTop: 0.5,
                  width: 260,
                  minWidth: 260,
                  maxWidth: 260,
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <Box sx={{ padding: sizeByBreakpoint(0.75, 14) }}>
                    <Stack direction="column" gap={sizeByBreakpoint(0.75, 14)}>
                      <CustomListItem
                        Icon={FileTextOutlined}
                        primaryText="Documentation"
                        onClick={() => handleMenuItemClick('https://docs.tradersconnect.com')}
                      />
                      <CustomListItem
                        Icon={MessageOutlined}
                        primaryText="Live Chat"
                        onClick={() => {
                          setOpen(false);
                          Crisp.chat.show();
                          Crisp.chat.open();
                        }}
                      />
                      <CustomListItem
                        Icon={WhatsAppOutlined}
                        primaryText="WhatsApp"
                        onClick={() => handleMenuItemClick('https://wa.me/447418610584')}
                      />
                      <CustomListItem
                        Icon={SendOutlined}
                        primaryText="Telegram"
                        onClick={() => handleMenuItemClick('https://telegram.me/tradersconnect_bot')}
                      />

                      <Box
                        sx={{
                          borderTop: `1px solid ${theme.palette.divider}`,
                          paddingTop: sizeByBreakpoint(0.75, 14),
                          cursor: 'pointer',
                          '&:hover': {
                            '& .MuiListItemIcon-root': {
                              color: theme.palette.primary.main,
                              transition: 'color 100ms ease-in-out'
                            },
                            '& .MuiListItemText-root': {
                              color: theme.palette.primary.main,
                              transition: 'color 100ms ease-in-out'
                            }
                          }
                        }}
                        onClick={() => handleMenuItemClick('https://status.tradersconnect.com')}
                      >
                        <CustomListItem
                          Icon={status === 'operational' ? CheckCircleOutlined : WarningOutlined}
                          primaryText="Status"
                          showDot={true}
                          dotColor={getStatusColor()}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: textSizeByBreakpoint(12),
                            ml: sizeByBreakpoint(1.5, 32),
                            display: 'block',
                            mt: 0.5
                          }}
                        >
                          {getStatusText()}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </ClickAwayListener>
              </Paper>
            )}
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Support;
